import { useState, useCallback, useRef, useEffect } from 'react';
import { Assessment, AssessmentStatusResponse, AssessmentResults, SalesStrategy, StartAssessmentInput } from '../types';
import * as assessmentsService from '../services/assessments';

export function useAssessment() {
  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [status, setStatus] = useState<AssessmentStatusResponse | null>(null);
  const [results, setResults] = useState<AssessmentResults | null>(null);
  const [strategies, setStrategies] = useState<SalesStrategy[] | null>(null);
  const [roleArn, setRoleArn] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const startAssessment = async (input: StartAssessmentInput): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      setResults(null);
      const newAssessment = await assessmentsService.startAssessment(input);
      setAssessment(newAssessment);
      startPolling(newAssessment.id);
    } catch (err) {
      setError('Error al iniciar el assessment');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const startPolling = useCallback((assessmentId: string) => {
    stopPolling();
    
    const poll = async () => {
      try {
        const statusData = await assessmentsService.getAssessmentStatus(assessmentId);
        setStatus(statusData);

        if (statusData.status === 'COMPLETED' || statusData.status === 'FAILED') {
          stopPolling();
          
          if (statusData.status === 'COMPLETED') {
            const resultsData = await assessmentsService.getAssessmentResults(assessmentId);
            setResults(resultsData.results);
            setStrategies(resultsData.strategies || null);
            setRoleArn(resultsData.roleArn || null);
          }
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    };

    // Initial poll
    poll();
    
    // Poll every 5 seconds
    pollingRef.current = setInterval(poll, 5000);
  }, [stopPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const reset = useCallback(() => {
    stopPolling();
    setAssessment(null);
    setStatus(null);
    setResults(null);
    setStrategies(null);
    setRoleArn(null);
    setError(null);
  }, [stopPolling]);

  return {
    assessment,
    status,
    results,
    strategies,
    roleArn,
    loading,
    error,
    startAssessment,
    reset,
  };
}
